import { PersonGroup, PhxMobileSelectComponent } from 'phx-core';
import isEqual from 'lodash/isEqual';
import { BehaviorSubject, combineLatest, race, Subject, timer, Observable } from 'rxjs';
import { debounce, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import {
  Component,
  EventEmitter,
  HostBinding,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { PhxSelectOption } from 'phx-core/lib/select/select.types';

import {
  DEFAULT_ALWAYS_SHOW_CONDENSED_MESSAGE, DEFAULT_DISABLED_TOOLTIP_MESSAGE, DEFAULT_EMIT_ONLY_INDEPENDENT_SELECTIONS,
  DEFAULT_FOOTER_THRESHOLD, DEFAULT_FORM_VARIANT, DEFAULT_FULL_WIDTH, DEFAULT_IS_DISABLED, ISelectOption,
} from '@app/misc/hierarchy-multi-select-with-counter/hierarchy-multi-select-with-counter.component';

import { Session } from '@app/services/session';
import { AdminFeatureFlagService } from '@app/services/feature-flags/admin';
import { AdminGroupService, IGroup } from '@app/admin/groups/admin-group.service';
import { GlobalGroupsService } from '@app/services/global-groups.service';


export const DEFAULT_DEBOUNCE_TIME: number = 0;
export const DEFAULT_PLACEHOLDER: string = $localize`Select group`;
export const DEFAULT_CONDENSED_MESSAGE: string = $localize`Groups`;
export const DEFAULT_SHOW_PREFIX_ICON: boolean = true;
export const DEFAULT_SHOW_COUNTER: boolean = true;
export const DEFAULT_EXPAND_GROUPS_FILTER: boolean = true;

@Component({ template: '' })
export abstract class GroupsMultiSelectFilterBaseComponent implements OnInit, OnDestroy {
  @HostBinding('class.w-100') @Input() public fullWidth: boolean = DEFAULT_FULL_WIDTH;
  @ViewChild(PhxMobileSelectComponent) public mobileSelect: PhxMobileSelectComponent;
  @Input() public isDisabled: boolean = DEFAULT_IS_DISABLED;
  @Input() public disabledTooltipMessage: string = DEFAULT_DISABLED_TOOLTIP_MESSAGE;
  /** If a higher-level option is selected, whether to filter out its sub-options in the output (default: true). */
  @Input() public emitOnlyIndependentSelections: boolean = DEFAULT_EMIT_ONLY_INDEPENDENT_SELECTIONS;
  @Input() public placeholder: string = DEFAULT_PLACEHOLDER;
  @Input() public condensedMessage: string = DEFAULT_CONDENSED_MESSAGE;
  /** Whether to show the condensed message even if only one option is selected (default: false). */
  @Input() public alwaysShowCondensedMessage: boolean = DEFAULT_ALWAYS_SHOW_CONDENSED_MESSAGE;
  @Input() public showPrefixIcon: boolean = DEFAULT_SHOW_PREFIX_ICON;
  @Input() public formVariant: boolean = DEFAULT_FORM_VARIANT;
  /** The minimum number of options which must be selected to show the footer banner (default: 2). */
  @Input() public footerThreshold: number = DEFAULT_FOOTER_THRESHOLD;
  /** Whether to show the text in the dropdown trigger (default: true) */
  @Input() public expandGroupsFilter: boolean = DEFAULT_EXPAND_GROUPS_FILTER;
  @Input() public debounceTime: number = DEFAULT_DEBOUNCE_TIME;
  /** Contains only permissible group IDs. Used to filter out group IDs that are not permissible. */
  @Input() public whitelistedGroupIds: number[] = undefined;
  @Input() public dialogOpen: boolean = false;
  @Input() public showCounter: boolean = DEFAULT_SHOW_COUNTER;
  @Input() public customTriggerButtonTemplate: TemplateRef<any>;
  @Input() public customLabelTemplate: TemplateRef<any>;
  @Input() public phxImplementation: boolean = false;
  @Input() public dropdownWidth: number;
  @Input() public showMobileView: boolean = false;
  /** Dropdown placement wrt search input. (Possible values: bottomLeft, bottomRight, topLeft, topRight) (default: bottomLeft) */
  @Input() public placement: 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight' = 'bottomLeft';
  @Output() public readonly dialogClose: EventEmitter<void> = new EventEmitter();
  @Output() public readonly selectedOptionsChanged: EventEmitter<Set<ISelectOption | PhxSelectOption>> = new EventEmitter();
  public readonly PersonGroupIcon: string = PersonGroup;

  public isLoading: boolean = false;
  public isDropdownOpen: boolean = false;
  public groups$: BehaviorSubject<IGroup[]> = new BehaviorSubject([]);
  public options$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject([]);
  public selectedGroupIds$: BehaviorSubject<Set<number>> = new BehaviorSubject(null);
  public hasChanged: boolean = false;
  public experimentalImplementation: boolean = false;
  public overlayClassName: string = '';
  public options: PhxSelectOption[] = [];
  public selectedOptions = [];
  protected groupService: AdminGroupService;
  protected globalGroupsService: GlobalGroupsService;
  protected destroyed$: Subject<void> = new Subject();

  /** Group IDs which were selected by the parent component and not the by user interaction. */
  protected parentSelectedGroupIds$: BehaviorSubject<number[]> = new BehaviorSubject([]);
  private session: Session;
  private dialogClose$: Subject<void> = new Subject();

  @HostBinding('class.overflow-hidden') overflowHidden: boolean = !this.phxImplementation;

  constructor(
    injector: Injector,
    private adminFeatureFlagService: AdminFeatureFlagService,
    ) {
    this.groupService = injector.get(AdminGroupService);
    this.globalGroupsService = injector.get(GlobalGroupsService);
    this.session = injector.get(Session);
    this.experimentalImplementation = this.adminFeatureFlagService.isGroupFilterInNewHeaderEnabled();
  }

  public get selectedOptionsCount(): number {
    return this.mobileSelect?.selectedOptionCount;
  }

  public get filterLabel(): string {
    return this.mobileSelect?.selectLabel as string;
  }

  public ngOnInit(): void {
    this.getFilteredGroupHierarchy(this.whitelistedGroupIds);

    combineLatest([this.groups$, this.parentSelectedGroupIds$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([groups, selectedGroupIds]) => this.onGroupsUpdated(groups, new Set(selectedGroupIds)));

    this.selectedGroupIds$
      .pipe(
        takeUntil(this.destroyed$),
        debounce((): Observable<number | void> => race(timer(this.debounceTime), this.dialogClose$)),
        distinctUntilChanged((prev: Set<number>, cur: Set<number>): boolean => isEqual(prev, cur)),
      )
      .subscribe((selectedGroupIds: Set<number>): void => this.onSelectedGroupIdsUpdated(selectedGroupIds));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const areWhitelistedGroupIdsChanged: boolean = changes.whitelistedGroupIds
      && !isEqual(changes.whitelistedGroupIds.currentValue, changes.whitelistedGroupIds.previousValue);
    if (!areWhitelistedGroupIdsChanged) return;

    this.getFilteredGroupHierarchy(changes.whitelistedGroupIds.currentValue);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
  }

  public onDialogClose(): void {
    this.dialogClose.emit();
  }

  public onDropDownClose(): void {
    this.dialogClose$.next();
  }

  // this method will be removed in feature flag cleanup
  /**
   * Save the IDs of the selected groups.
   * @param selectedOptions The selected groups (as options).
   */
  public onSelectedGroupsUpdate(selectedOptions: Set<ISelectOption>): void {
    const selectedGroupIds: Set<number> = new Set();
    selectedOptions.forEach((option: ISelectOption) => selectedGroupIds.add(parseInt(option.key, 10)));
    this.hasChanged = true;
    this.selectedGroupIds$.next(selectedGroupIds);
    this.selectedOptionsChanged.emit(selectedOptions);
  }

  public onPhoenixSelectChange(selectedOptions: PhxSelectOption[]): void {
    this.hasChanged = true;
    this.selectedOptions = selectedOptions.map(opt => opt.value);
    this.selectedGroupIds$.next(new Set(this.selectedOptions));
    this.selectedOptionsChanged.emit(new Set(selectedOptions));
  }

  public onDropdownOpenChange(isOpen: boolean): void {
    this.isDropdownOpen = isOpen;
  }

  public onDialogOpenChange(isOpen: boolean): void {
    this.dialogOpen = isOpen;
    if (!isOpen) this.onDialogClose();
  }
  /**
   * Handle the selected group IDs being updated.
   * @param selectedGroupIds The selected group IDs.
   */
  protected abstract onSelectedGroupIdsUpdated(selectedGroupIds: Set<number>): void;

  /**
   * Handle the group hierarchy being updated.
   * @param groups The group hierarchy.
   */
  protected abstract onGroupsUpdated(groups: IGroup[], parentSelectedGroupIds: Set<number>): void;

  /**
   * Transform a group to its corresponding option object.
   * @param group The group.
   * @param selectedGroupIds The selected group IDs.
   * @returns The option.
   */
  protected transformGroupToOptions(group: IGroup, selectedGroupIds: Set<number>): ISelectOption {
    return {
      key: group.id.toString(),
      label: group.name,
      isPreSelected: selectedGroupIds.has(group.id),
      areChildrenSingleSelect: true,
      children: (group.children || []).map(
        (child: IGroup) => this.transformGroupToOptions(child, selectedGroupIds),
      ),
    };
  }

  protected transformToPhoenixOption(group: IGroup): PhxSelectOption {
    return {
      value: group.id,
      label: group.name,
      children: (group.children || []).map(
        (child: IGroup) => this.transformToPhoenixOption(child),
      ),
    };
  }

  /**
   * Get the permissible group options using permissibleGroupIds.
   * @param permissibleGroupIds The IDs of the groups which should be included. If empty, include all groups.
   */
  private getFilteredGroupHierarchy(whitelistedGroupIds: number[]): void {
    this.isLoading = true;
    this.groupService.getGroupHierarchy(whitelistedGroupIds)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((groups: IGroup[]): void => {
        this.groups$.next(groups);
        this.globalGroupsService.updateData(groups);
        if (this.phxImplementation) this.options = groups.map((group: IGroup) => this.transformToPhoenixOption(group));
        this.isLoading = false;
      });
  }
}
